// "Livraisons" tab — every delivery feeding the OT, with the same
// compliance fields the legacy delivery list shows (traceability %,
// risk flag, lock state, EUDR verdict).

import { useTranslation } from "react-i18next"
import { Ship } from "lucide-react"

import { Card } from "@/components/ui/card"
import type {
  OTTraceability,
  TraceabilityDeliveryDigest,
} from "@/features/transit/api/types"

interface Props {
  traceability: OTTraceability
}

export function TraceabilityDeliveriesTab({ traceability }: Props) {
  const { t } = useTranslation("transit")
  const deliveries = traceability.deliveries

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <Ship className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold tracking-tight text-foreground">
          {t("trace.deliveries.title", { count: deliveries.length })}
        </h2>
      </div>

      {deliveries.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {t("trace.deliveries.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.deliveries.col.delivery")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.deliveries.col.coop")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.deliveries.col.shipped")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.deliveries.col.bags")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.deliveries.col.kg")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.deliveries.col.producers")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.deliveries.col.purchases")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.deliveries.col.trace")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.deliveries.col.risks")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.deliveries.col.eudr")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {deliveries.map((d) => (
                <Row key={d.id} digest={d} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function Row({ digest }: { digest: TraceabilityDeliveryDigest }) {
  const { t } = useTranslation("transit")
  return (
    <tr className="text-foreground">
      <td className="px-3 py-2">
        <div className="font-medium">
          {digest.billOfLadingNumber ??
            digest.shippingFormNumber ??
            `#${digest.id}`}
        </div>
        {digest.shippingFormNumber && digest.billOfLadingNumber ? (
          <div className="text-[10px] text-muted-foreground">
            {digest.shippingFormNumber}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {digest.cooperativeName ?? "—"}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {digest.shippingDate ?? digest.dateOfExport ?? "—"}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {digest.numberOfBagsAttached?.toLocaleString() ?? "—"}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {digest.netKilogramsAttached != null
          ? digest.netKilogramsAttached.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })
          : "—"}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {digest.producerCount}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {digest.purchaseCount}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {digest.traceabilityStatus}%
      </td>
      <td className="px-3 py-2">
        {digest.hasRisks ? (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
            {t("trace.deliveries.risksKo")}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            {t("trace.deliveries.risksOk")}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <Badge digest={digest} />
      </td>
    </tr>
  )
}

function Badge({ digest }: { digest: TraceabilityDeliveryDigest }) {
  const { t } = useTranslation("transit")
  if (digest.isCompliant) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
        {t("terminer.traceDelivery.badgeCompliant")}
      </span>
    )
  }
  if (!digest.isLocked) {
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
        {t("terminer.traceDelivery.badgeOpen")}
      </span>
    )
  }
  if (digest.hasRisks) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
        {t("terminer.traceDelivery.badgeRisk")}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
      {t("terminer.traceDelivery.badgePartial")}
    </span>
  )
}
